import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Snackbar, useMediaQuery } from '@mui/material'
import styled from '@emotion/styled'

import { useMapContext } from '../../context/MapContext'
import { useUpdateMapSource } from '../../hooks/useUpdateMapSource'
import { useConfigureMapLayers } from '../../hooks/useConfigureMapLayers'
import { useTreeNavigation } from '../../hooks/useTreeNavigation'
import { useTreeFromURL } from '../../hooks/useTreeFromURL'
import { useTreeSelection } from '../../hooks/useTreeSelection'
import { useDraggableMarkerOnClick } from '../../hooks/useDraggableMarkerOnClick'
import { useMapZoomLimits } from '../../hooks/useMapZoomLimits'
import { useMapData } from '../../hooks/useMapData'
import { useMapResize } from '../../hooks/useMapResize'
import { useLightPresetControl } from '../../hooks/useLightPresetControl'
import { useModelTooltip } from '../../hooks/useModelTooltip'
import { useMapboxSatellite } from '../../hooks/useMapboxSatellite'
import { usePointsOnlyMode } from '../../hooks/usePointsOnlyMode'
import { useTreeCodeLabels } from '../../hooks/useTreeCodeLabels'
import { useFitMapToGeojson } from '../../hooks/useFitMapToGeojson'
import { useFlyToCity } from '../../hooks/useFlyToCity'

// prettier-ignore
import { useCityBoundaries, useApplyCityBounds, useAddBoundaryLayer } from '../../hooks/useCityBoundaries'
import { useCityStatusBar } from '../../hooks/useCityStatusBar'
import { useTenantBoundary } from '../../hooks/useTenantBoundary'
import { useCities } from '../../context/CitiesContext'
import { useAuth } from '../../context/AuthProvider'
import * as RT from '../../config/routeType'

import { useMapControls } from '../../hooks/useMapControls'
import { useCityReset } from '../../hooks/useCityReset'
import { buildCantonUrl, normalizePathname } from '../../utils/cantonUrl'
import { hasSavedViewportForCity } from '../../utils/mapViewport'
import { useTenantAutoConfig } from '../../hooks/useTenantAutoConfig'
import { useFlyToPopup } from '../../hooks/useFlyToPopup'
import { useTreeStatsBarAnchor } from '../../hooks/useTreeStatsBarAnchor'
import { useLoadMapData } from '../../hooks/useLoadMapData'
import { useFormTreeMarker } from '../../hooks/useFormTreeMarker'
import { useCaptureLocationRings } from '../../hooks/useCaptureLocationRings'
import { useRelocationDistance } from '../../hooks/useRelocationDistance'

import CenterMarkerOverlay from './CenterMarkerOverlay'
import ZoomDebugOverlay from './ZoomDebugOverlay'
import PopupContentContainer from '../PopupContentContainer'
import DrawerRightInfo from '../../components/DrawerRightInfo'
import LoginModal from '../../components/LoginModal'
import DeleteRecordModal from '../../components/DeleteRecordModal'
import LoadingModal from '../LoadingModal'
import AlertCustom from '../Alerts'
import PopoverSearch from '../PopoverSearch'

import { AlertContext } from '../../context/AlertContext'
import {
  setTreeCoords,
  setShowAccessAlert,
  setShowTenantAlert,
  setPopup,
  setAddMode,
} from '../../redux/slices/Map/map'
import { show as openDrawerRight } from '../../redux/slices/Drawer/drawerRight'

import { Alert } from '@mui/material'
import AccessRestrictedSnackbar from '../AccessRestrictedSnackbar'

import './MapControls.css'
import '../../components/MapboxGL/LightPresetControl.css'
import '../../components/ModelTooltip/ModelTooltip.css'

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`

const BOUNDARY_VISIBILITY_PREFS_KEY = 'ArbolEc_boundaryVisibilityPrefs'

const readBoundaryVisibilityPrefs = () => {
  const defaults = {
    cantonVisible: true,
    tenantVisible: true,
  }

  if (typeof window === 'undefined') return defaults

  try {
    const raw = window.sessionStorage.getItem(BOUNDARY_VISIBILITY_PREFS_KEY)
    if (!raw) return defaults

    const parsed = JSON.parse(raw)
    return {
      cantonVisible: Boolean(parsed?.cantonVisible),
      tenantVisible: Boolean(parsed?.tenantVisible),
    }
  } catch {
    return defaults
  }
}

const writeBoundaryVisibilityPrefs = state => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(
      BOUNDARY_VISIBILITY_PREFS_KEY,
      JSON.stringify({
        cantonVisible: Boolean(state?.cantonVisible),
        tenantVisible: Boolean(state?.tenantVisible),
      }),
    )
  } catch {
    // ignore
  }
}

// prettier-ignore
const MapboxGLMap = ({
  isAdmin,
  isAuth,
  pathname,
  widthMap = '100vw',
  heightMap = '100vh',
  store,
  minimalControls = false,
  forcedTreeCoords = null,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { treeId, iso2: urlIso2 } = useParams()

  // Detectar dispositivo táctil (móvil/tablet) para deshabilitar tooltip
  const isTouchDevice = useMediaQuery('(pointer: coarse)')

  const [popupLngLat, setPopupLngLat] = React.useState(null)
  const [propsContent, setPropsContent] = React.useState(null)

  const lastTenantFlyKeyRef = React.useRef(null)

  // Al entrar en la ruta de edición viniendo de otra ruta, limpiar el popup heredado
  const prevPathnameRef = React.useRef(pathname)
  React.useEffect(() => {
    const wasEdit = prevPathnameRef.current.includes(RT.MANAGEMENT_TREE)
    const isEdit = pathname.includes(RT.MANAGEMENT_TREE)
    prevPathnameRef.current = pathname
    if (!wasEdit && isEdit) {
      dispatch(setPopup({ open: false, code: null, id: null }))
      setPopupLngLat(null)
      setPropsContent(null)
    }
  }, [pathname, dispatch])

  const { alertRef } = React.useContext(AlertContext)
  const { map, setMapContainer, mapLoaded } = useMapContext()
  const { getEnabledCities, getEnabledCantones, getTenantsByCantonSlug, selectedTenant, selectTenant, resetTenant, loading: citiesLoading, selectedCountry, selectCountry, getUniqueCountries, } = useCities()
  const { allowedCantonSlugs, getAssignedTenantsForCanton, isResearcher } = useAuth()

  // Comparación verificados/no verificados solo para usuarios autenticados.
  // Visitantes mantienen los puntos verdes del mapa público.
  const canCompareVerified = Boolean(isAuth)

  const { addMode, coords, showAccessAlert, showTenantAlert } = useSelector(state => state.mapState)
  const lightPreset = useSelector(state => state.mapState.lightPreset)
  const appBarHeight = useSelector(state => state.drawerLeft.drawerHeight)

  const safeWidth = widthMap || '100vw'
  const safeHeight = heightMap || '100vh'
  const initialBoundaryPrefsRef = React.useRef(readBoundaryVisibilityPrefs())

  const [cantonBoundaryVisible, setCantonBoundaryVisible] = React.useState(
    initialBoundaryPrefsRef.current.cantonVisible
  )
  const [tenantBoundaryVisible, setTenantBoundaryVisible] = React.useState(
    initialBoundaryPrefsRef.current.tenantVisible
  )

  useLightPresetControl({ map, enabled: lightPreset.enabled && mapLoaded && !minimalControls, position: 'top-left' })

  const isRegisterPage = pathname.includes(RT.REGISTER_TREE)
  const [legendVisibility, setLegendVisibility] = React.useState({
    biomass: false,
  })
  const mapLegendItems = React.useMemo(() => {
    if (isRegisterPage || !isAdmin) return []

    return [
      {
        id: 'biomass',
        label: 'Impacto ambiental',
        visible: legendVisibility.biomass !== false,
      },
    ]
  }, [isAdmin, isRegisterPage, legendVisibility.biomass])
  const handleLegendToggle = React.useCallback((legendId, visible) => {
    setLegendVisibility(current => ({
      ...current,
      [legendId]: visible,
    }))
  }, [])

  const { currentMapData, isUsingFilters } = useMapData(selectedTenant)
  const {
    filterAnchorEl,
    setFilterAnchorEl,
    updateBoundaryControlState,
    updateBoundaryToggleCallback,
    setBoundaryVisibility,
  } = useMapControls(map, mapLoaded, store, {
    minimalControls,
    alertRef,
    legendItems: mapLegendItems,
    onLegendToggle: handleLegendToggle,
  })
  const { cityCenter, cityZoom, cityName, currentCity, cityBounds, cityBoundary, loading: cityBoundaryLoading } = useCityBoundaries()

  // Obtener tenants del cantón actual para el selector (DEBE estar antes de handleTenantChange)
  const currentTenants = React.useMemo(
    () => getTenantsByCantonSlug(currentCity),
    [getTenantsByCantonSlug, currentCity]
  )

  // Obtener cantones con tenants para el dropdown encadenado
  const cantonesWithTenants = React.useMemo(
    () => getEnabledCantones(),
    [getEnabledCantones]
  )

  // Vista principal del cantón: comparar contra la URL canónica (con prefijo de país)
  // generada desde los datos, conservando la ruta legacy sin país.
  const normalizedPathname = React.useMemo(() => normalizePathname(pathname), [pathname])
  const isCityMainMap =
    Boolean(currentCity) &&
    (normalizedPathname === normalizePathname(buildCantonUrl(currentCity, cantonesWithTenants)) ||
      normalizedPathname === `/${currentCity}`)

  // Re-centrar mapa cuando cambia la ciudad. Se omite cuando:
  // - hay un tenant válido seleccionado en la vista principal (el centrado/zoom lo
  //   gestiona el efecto del tenant; este vuelo pisaría su zoom al llegar cityCenter async);
  // - hay un árbol/popup activo (no competir con el vuelo al popup, ej. deep-link a /tree/CODE);
  // - un único tenant real (con centro) va a tomar el centrado (evita el doble vuelo en el
  //   primer load: cantón -> tenant).
  const hasValidSelectedTenant = currentTenants.some(t => t.slug === selectedTenant)
  const hasActiveTreeSelection = Boolean(popupLngLat) || Boolean(treeId)
  const realTenants = currentTenants.filter(t => !t.isGeneric)
  const tenantWillOwnCentering =
    isCityMainMap && realTenants.length === 1 && Boolean(realTenants[0].center)
  useFlyToCity(map, mapLoaded, currentCity, cityCenter, cityZoom, {
    skip: (isCityMainMap && hasValidSelectedTenant) || hasActiveTreeSelection || tenantWillOwnCentering,
  })

  useCityReset(currentCity)

  const countries = React.useMemo(() => getUniqueCountries(), [getUniqueCountries])
  const selectedCountryData = React.useMemo(
    () => (selectedCountry ? countries.find(c => c.iso2 === selectedCountry) : null),
    [selectedCountry, countries]
  )

  // Sincronizar país de la URL con el contexto (urlIso2 es fuente de verdad)
  React.useEffect(() => {
    if (!urlIso2) return
    const iso2Upper = urlIso2.toUpperCase()
    if (iso2Upper !== selectedCountry) selectCountry(iso2Upper)
  }, [urlIso2]) // eslint-disable-line react-hooks/exhaustive-deps

  // Detectar si estamos en ruta protegida (registro/edición)
  const isOnProtectedRoute = pathname.includes(RT.REGISTER_TREE) || pathname.includes(RT.MANAGEMENT_TREE)

  // Filtrar ciudades del dropdown: en rutas protegidas, solo ver cantones asignados
  const statusBarCities = React.useMemo(() => {
    const allCities = getEnabledCities()
    if (isOnProtectedRoute && allowedCantonSlugs) {
      return allCities.filter(c => allowedCantonSlugs.includes(c.slug))
    }
    return allCities
  }, [getEnabledCities, isOnProtectedRoute, allowedCantonSlugs])

  // Filtrar tenants del dropdown: en rutas protegidas, solo ver tenants asignados.
  // El tenant genérico (ÁrbolEc temporal) solo es visible para admins e investigadores.
  const statusBarTenants = React.useMemo(() => {
    let tenants = currentTenants
    if (isOnProtectedRoute) {
      const assignedForCanton = getAssignedTenantsForCanton(currentCity)
      if (assignedForCanton) {
        tenants = tenants.filter(t => assignedForCanton.some(at => at.tenantSlug === t.slug))
      }
    }
    if (!isAdmin && !isResearcher) {
      tenants = tenants.filter(t => !t.isGeneric)
    }
    return tenants
  }, [isOnProtectedRoute, currentCity, currentTenants, getAssignedTenantsForCanton, isAdmin, isResearcher])

  // Fly al país cuando cambia selectedCountry (solo en /:iso2 sin cantón)
  React.useEffect(() => {
    if (!map || !mapLoaded || !selectedCountryData?.center || currentCity) return
    map.flyTo({
      center: selectedCountryData.center,
      zoom: selectedCountryData.zoom || 3,
      essential: true,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
    })
  }, [map, mapLoaded, selectedCountry, selectedCountryData, currentCity]) // eslint-disable-line react-hooks/exhaustive-deps

  useTenantAutoConfig({
    selectedTenant,
    citiesLoading,
    currentTenants,
    resetTenant,
    currentCity,
    allowedCantonSlugs,
    isOnProtectedRoute,
    statusBarTenants,
    selectTenant,
    getAssignedTenantsForCanton,
    // En páginas de edición, el formulario gestiona el tenant de forma independiente
    // (reasignación cross-cantón). Desactivar la validación/auto-selección del hook
    // para evitar que resetee el tenant cuando el usuario cambia de cantón en el formulario.
    disabled: pathname.includes(RT.MANAGEMENT_TREE),
  })

  // Obtener bounds del tenant seleccionado
  const selectedTenantData = React.useMemo(() => {
    if (!selectedTenant || !currentTenants.length) return null
    return currentTenants.find(t => t.slug === selectedTenant)
  }, [selectedTenant, currentTenants])

  // En la vista principal del cantón (/:city), si existe tenant en sesión,
  // centrar una vez al tenant para evitar quedar en el centro general del cantón.
  React.useEffect(() => {
    if (!map || !mapLoaded || !isCityMainMap || !currentCity) return

    if (!selectedTenant) {
      lastTenantFlyKeyRef.current = `${currentCity}::__all__`
      return
    }

    const tenant = currentTenants.find(t => t.slug === selectedTenant)

    // Tenant genérico sin centro propio: usar el centro del cantón como fallback
    const effectiveCenter = tenant?.center || (tenant?.isGeneric && cityCenter ? cityCenter : null)
    const effectiveZoom = tenant?.zoom || (tenant?.isGeneric && cityZoom ? cityZoom : 15)

    if (!effectiveCenter) return

    const flyKey = `${currentCity}:${selectedTenant}:${effectiveCenter.join(',')}:${effectiveZoom}`
    if (lastTenantFlyKeyRef.current === flyKey) return

    // Respetar el zoom guardado en la sesión: no recentrar al tenant en recargas.
    // Los cambios deliberados de tenant usan executeTenantChange (vuelo imperativo).
    if (hasSavedViewportForCity(currentCity)) {
      lastTenantFlyKeyRef.current = flyKey
      return
    }

    map.flyTo({
      center: effectiveCenter,
      zoom: effectiveZoom,
      essential: true,
      padding: { top: 50, bottom: 50, left: 50, right: 350 },
    })

    lastTenantFlyKeyRef.current = flyKey
  }, [map, mapLoaded, isCityMainMap, currentCity, selectedTenant, currentTenants, cityCenter, cityZoom])

  // Colores para los boundaries (pueden venir de configuración en el futuro)
  const BOUNDARY_COLORS = React.useMemo(() => ({
    canton: '#f34b21',  // Rojo para cantón
    tenant: '#1976d2',  // Azul para tenant
  }), [])
  const isTenantMode = Boolean(selectedTenant)
  const effectiveTenantBoundaryVisible = isTenantMode && tenantBoundaryVisible

  // Hook para gestionar el boundary del tenant seleccionado
  const {
    boundaryData: tenantBoundaryData,
    isGeneric: tenantIsGeneric,
    loading: tenantBoundaryLoading,
    error: tenantBoundaryError,
    setVisibility: setTenantBoundaryVisibility,
  } = useTenantBoundary(map, mapLoaded, {
    selectedTenant,
    tenantBounds: selectedTenantData?.bounds,
    cantonBounds: cityBounds,
    isAdmin,
    initialVisible: effectiveTenantBoundaryVisible,
    style: {
      lineColor: BOUNDARY_COLORS.tenant,
      lineWidth: 3,
      fillColor: BOUNDARY_COLORS.tenant,
      fillOpacity: 0.1,
      beforeLayer: 'clusters',
    },
  })

  // El botón sólo se muestra activo si la preferencia está ON *y* el boundary
  // realmente existe o se está cargando (no hay error / sin datos).
  const cantonBoundaryAvailable = cityBoundaryLoading || Boolean(cityBoundary)
  const tenantBoundaryAvailable = tenantBoundaryLoading || (!tenantBoundaryError && Boolean(tenantBoundaryData))
  const activeBoundaryVisible = isTenantMode
    ? (tenantBoundaryVisible && tenantBoundaryAvailable)
    : (cantonBoundaryVisible && cantonBoundaryAvailable)

  const handleBoundaryToggle = React.useCallback(visible => {
    const nextVisible = Boolean(visible)
    if (isTenantMode) {
      setTenantBoundaryVisible(nextVisible)
    } else {
      setCantonBoundaryVisible(nextVisible)
    }
  }, [isTenantMode])

  // Persistir preferencias por sesión (tenant/cantón por separado)
  React.useEffect(() => {
    writeBoundaryVisibilityPrefs({
      cantonVisible: cantonBoundaryVisible,
      tenantVisible: tenantBoundaryVisible,
    })
  }, [cantonBoundaryVisible, tenantBoundaryVisible])

  // Cantón visible solo cuando no hay tenant seleccionado (modo "Todos")
  React.useEffect(() => {
    const visibleInMap = !isTenantMode && cantonBoundaryVisible
    setBoundaryVisibility(visibleInMap)

    if (!map || !mapLoaded || !currentCity) return undefined

    // Reintento corto: la capa de boundary puede añadirse unos ms después
    const timeoutId = setTimeout(() => {
      setBoundaryVisibility(visibleInMap)
    }, 250)

    return () => clearTimeout(timeoutId)
  }, [map, mapLoaded, currentCity, isTenantMode, cantonBoundaryVisible, setBoundaryVisibility])

  // Tenant visible cuando hay tenant seleccionado y su preferencia está activa
  React.useEffect(() => {
    setTenantBoundaryVisibility(effectiveTenantBoundaryVisible)
  }, [effectiveTenantBoundaryVisible, setTenantBoundaryVisibility])

  // Sincronizar estado visual del botón nativo según modo activo
  // mapLoaded en deps: el control se crea cuando mapLoaded=true, por lo que
  // este effect debe re-ejecutarse en ese momento para no llamar en vano con boundary=null
  React.useEffect(() => {
    if (!mapLoaded) return
    updateBoundaryControlState({
      mode: isTenantMode ? 'tenant' : 'canton',
      visible: Boolean(activeBoundaryVisible),
    })
  }, [mapLoaded, updateBoundaryControlState, isTenantMode, activeBoundaryVisible])

  // Sincronizar callback activo del botón nativo
  React.useEffect(() => {
    if (!mapLoaded) return
    updateBoundaryToggleCallback(handleBoundaryToggle)
  }, [mapLoaded, updateBoundaryToggleCallback, handleBoundaryToggle])

  // Ejecutar el cambio de cantón (lógica real)
  const executeCityChange = React.useCallback(slug => {
    if (!slug || slug === currentCity) return
    resetTenant()
    dispatch(setPopup({ open: false, code: null, id: null }))
    dispatch(openDrawerRight(false))
    setPopupLngLat(null)
    setPropsContent(null)
    if (isOnProtectedRoute) {
      // Resetear captura al cambiar de cantón
      dispatch(setAddMode(false))
      dispatch(setTreeCoords(null))
      const routeSuffix = pathname.includes(RT.REGISTER_TREE) ? `/${RT.REGISTER_TREE}` : ''
      navigate(buildCantonUrl(slug, cantonesWithTenants, routeSuffix))
    } else {
      navigate(buildCantonUrl(slug, cantonesWithTenants))
    }
  }, [navigate, currentCity, resetTenant, isOnProtectedRoute, pathname, dispatch, cantonesWithTenants])

  // Callback para cambiar de cantón desde el selector.
  const handleCityChange = React.useCallback(slug => {
    if (!slug || slug === currentCity) return
    executeCityChange(slug)
  }, [currentCity, executeCityChange])

  // Ejecutar el cambio de tenant (lógica real)
  const executeTenantChange = React.useCallback(tenantSlug => {
    selectTenant(tenantSlug)

    // Cerrar popup y drawer al cambiar de tenant
    dispatch(setPopup({ open: false, code: null, id: null }))
    dispatch(openDrawerRight(false))
    setPopupLngLat(null)
    setPropsContent(null)

    if (isOnProtectedRoute) {
      // Resetear captura al cambiar de tenant (botón vuelve a estado inicial)
      dispatch(setAddMode(false))
      dispatch(setTreeCoords(null))
    } else if (currentCity) {
      // En ruta normal: navegar a la raíz del cantón
      navigate(buildCantonUrl(currentCity, cantonesWithTenants), { replace: true })
    }

    // Verificar que el mapa exista antes de hacer flyTo
    if (!map) return

    // Si hay un tenant seleccionado, buscar sus coordenadas y hacer flyTo
    if (tenantSlug) {
      // Siempre reactivar el boundary del tenant al seleccionarlo
      setTenantBoundaryVisible(true)

      const tenant = currentTenants.find(t => t.slug === tenantSlug)

      if (tenant?.center) {
        const tenantZoom = tenant.zoom || 15
        const flyKey = `${currentCity}:${tenantSlug}:${tenant.center.join(',')}:${tenantZoom}`
        lastTenantFlyKeyRef.current = flyKey
        map.flyTo({
          center: tenant.center,
          zoom: tenantZoom,
          essential: true,
          padding: { top: 50, bottom: 50, left: 50, right: 350 }
        })
      }
    } else {
      // Volviendo a "Todos": siempre reactivar el boundary del cantón
      setCantonBoundaryVisible(true)

      lastTenantFlyKeyRef.current = currentCity ? `${currentCity}::__all__` : null
      // Sin tenant seleccionado → volver a la vista del cantón
      if (cityCenter) {
        map.flyTo({
          center: cityCenter,
          zoom: cityZoom || 12,
          essential: true,
          padding: { top: 0, bottom: 0, left: 0, right: 0 },
        })
      }
    }
  }, [selectTenant, currentTenants, map, cityCenter, cityZoom, dispatch, navigate, currentCity, isOnProtectedRoute, cantonesWithTenants, setCantonBoundaryVisible, setTenantBoundaryVisible])

  // Callback para cambiar de país: navegar a /:iso2 (la URL es la fuente de verdad)
  const handleCountryChange = React.useCallback(iso2 => {
    if (!iso2 || iso2.toLowerCase() === urlIso2?.toLowerCase()) return
    resetTenant()
    dispatch(setPopup({ open: false, code: null, id: null }))
    dispatch(openDrawerRight(false))
    setPopupLngLat(null)
    setPropsContent(null)
    // Limpiar bounds del cantón anterior antes de navegar para que el flyTo
    // al nuevo país no quede bloqueado/distorsionado por restricciones previas
    if (map) map.setMaxBounds(null)
    navigate(`/${iso2.toLowerCase()}`)
  }, [navigate, urlIso2, resetTenant, dispatch, map])

  // Callback para cambiar de tenant desde el selector.
  const handleTenantChange = React.useCallback(tenantSlug => {
    executeTenantChange(tenantSlug)
  }, [executeTenantChange])

  const handleCloseAccessAlert = React.useCallback(() => {
    dispatch(setShowAccessAlert(false))
  }, [dispatch])

  const handleContactForAccess = React.useCallback(() => {
    dispatch(setShowAccessAlert(false))
    navigate(`/${RT.CONTACT}`)
  }, [dispatch, navigate])

  useApplyCityBounds(map, mapLoaded, isAdmin)
  useAddBoundaryLayer(map, mapLoaded, { lineColor: '#f34b21', lineWidth: 2, fillColor: '#f32121', fillOpacity: 0.05, showFill: true, showLine: true, beforeLayer: 'clusters', initialVisible: !isTenantMode && cantonBoundaryVisible })
  useCityStatusBar(map, mapLoaded, {
    cityName,
    citySlug: currentCity,
    cityCenter,
    cityZoom,
    currentMapData,
    showDetails: isAdmin,
    hideStats: isRegisterPage,
    disabled: minimalControls,
    appBarHeight,
    cities: statusBarCities,
    cantones: cantonesWithTenants,
    countries,
    selectedCountry,
    currentTenants: statusBarTenants,
    selectedTenant,
    onCityChange: handleCityChange,
    onTenantChange: handleTenantChange,
    onCountryChange: handleCountryChange,
    openCityDropdown: !currentCity,
    cityLabel: selectedCountryData?.label_n2 || null,
    legendVisibility,
    showBiomassLegend: Boolean(isAdmin),
  })
  const effectiveZoom = currentCity ? cityZoom : (selectedCountryData?.zoom || 5)
  useMapZoomLimits({ map, mapLoaded, isAdmin, cityZoom: effectiveZoom })

  const popupLoading = useSelector(state => state.mapState.popup.loading)
  const { selectTree } = useTreeSelection({ map, dispatch, navigate, pathname, cantones: cantonesWithTenants, setPropsContent, setPopupLngLat, popupLoading })
  useTreeFromURL()
  const { layersReady } = useConfigureMapLayers({ map, canCompareVerified, setPropsContent, setPopupLngLat, pathname, dispatch, glbUrl: '/models/tree.glb', selectTree })

  // En modo edición, los aros permanecen siempre sobre el árbol del formulario (treeId de la URL),
  // sin importar qué otro árbol seleccione el usuario en el mapa.
  const isEditMode = pathname.includes(RT.MANAGEMENT_TREE)
  const editTreeFixedCoords = React.useMemo(() => {
    if (!isEditMode) return null
    if (
      Array.isArray(forcedTreeCoords) &&
      forcedTreeCoords.length === 2 &&
      Number.isFinite(forcedTreeCoords[0]) &&
      Number.isFinite(forcedTreeCoords[1])
    ) {
      return forcedTreeCoords
    }
    if (!treeId || !currentMapData?.features) return null
    const feature = currentMapData.features.find(f => String(f.properties?.id) === String(treeId))
    return feature?.geometry?.coordinates ?? null
  }, [isEditMode, forcedTreeCoords, treeId, currentMapData])

  useFormTreeMarker({ map, layersReady, selectedCoords: popupLngLat, fixedCoords: editTreeFixedCoords })
  useCaptureLocationRings({ map, layersReady })
  useRelocationDistance({ map, layersReady, originCoords: editTreeFixedCoords, enabled: isEditMode })

  useUpdateMapSource({ map, sourceId: 'trees', geojson: currentMapData, pathname, layersReady })
  useModelTooltip({ map, enabled: layersReady && !isTouchDevice })
  usePointsOnlyMode(map, layersReady)
  useTreeCodeLabels(map, layersReady)
  useTreeNavigation({ map, treeId, treeData: currentMapData, alertRef, selectTree, popupLngLat, isEditMode })
  useDraggableMarkerOnClick({ map, enabled: addMode, coords, onCoordsChange: (newCoords) => dispatch(setTreeCoords(newCoords)), selectedTenant })
  useMapResize(map, mapLoaded, [pathname, safeWidth, safeHeight, isAuth], 150)

  useFlyToPopup(map, mapLoaded, popupLngLat)

  const accessAlertAnchorPosition = useTreeStatsBarAnchor(map, showAccessAlert)

  const { loading } = useLoadMapData({
    map, mapLoaded, layersReady, currentCity,
    selectedTenant,
    isUsingFilters, alertRef,
    isEditMode,
  })

  useMapboxSatellite(map, mapLoaded, { beforeLayer: 'clusters' })

  const blockAutoFit = Boolean(treeId) || Boolean(popupLngLat)

  useFitMapToGeojson({
    map, mapLoaded, layersReady,
    geojson: currentMapData, enabled: isUsingFilters, padding: 90,
    maxZoom: 18, duration: 700, block: blockAutoFit,
  })

  return (
    <Box
      component="div"
      className={[
        isOnProtectedRoute ? 'hide-native-geolocate' : '',
        isCityMainMap ? 'city-main-map' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      sx={{
        width: safeWidth,
        height: safeHeight,
        display: 'flex',
        position: 'relative',
        transition: 'width 0.4s ease-in-out, height 0.4s ease-in-out',
      }}>
      <LoadingModal open={loading} />
      <MapContainer ref={setMapContainer} />
      <ZoomDebugOverlay map={map} />

      {addMode && (
        <CenterMarkerOverlay
          map={map}
          initialCoords={coords}
          onConfirm={newCoords => {
            dispatch(setTreeCoords(newCoords))
            dispatch(setAddMode(false))
          }}
          onCancel={() => dispatch(setAddMode(false))}
          selectedTenant={selectedTenant}
          tenantBoundary={tenantBoundaryData}
          tenantBoundaryLoading={tenantBoundaryLoading}
          tenantIsGeneric={tenantIsGeneric || Boolean(selectedTenantData?.isGeneric)}
        />
      )}

      <PopupContentContainer cityName={cityName} lngLat={popupLngLat} map={map} propsContent={propsContent} />

      {filterAnchorEl && (
        <PopoverSearch
          id="filter-popover"
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          isAuth={isAuth}
        />
      )}

      <DrawerRightInfo />
      <LoginModal />
      <DeleteRecordModal />

      <AlertCustom ref={alertRef} severity="info">
        {currentMapData?.features?.length
          ? `Hay ${currentMapData?.features?.length} registros`
          : 'No hay resultados'}
      </AlertCustom>

      <AccessRestrictedSnackbar
        open={showAccessAlert}
        onClose={handleCloseAccessAlert}
        onContact={handleContactForAccess}
        anchorPosition={accessAlertAnchorPosition}
      />

      <Snackbar
        open={showTenantAlert}
        autoHideDuration={6000}
        onClose={() => dispatch(setShowTenantAlert(false))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}>
        <Alert
          onClose={() => dispatch(setShowTenantAlert(false))}
          severity="warning"
          variant="filled">
          No tienes permisos para editar árboles de este lugar.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MapboxGLMap
